/* Source-backed Shimano document and part lookup. No inferred compatibility. */
(() => {
  'use strict';
  const norm = value => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const bucket = value => { let hash = 0; for (const c of norm(value)) hash = (hash * 31 + c.charCodeAt(0)) % 64; return String(hash).padStart(2, '0'); };
  const el = (tag, text, cls) => { const node = document.createElement(tag); if (text != null) node.textContent = text; if (cls) node.className = cls; return node; };
  const link = (text, url, cls, external = false) => { const a = el('a', text, cls); a.href = url; if (external) { a.target = '_blank'; a.rel = 'noopener noreferrer'; } return a; };
  const cached = new Map();
  async function json(url) { if (!cached.has(url)) cached.set(url, fetch(url, { credentials: 'same-origin' }).then(r => { if (!r.ok) throw new Error('Document data could not be loaded.'); return r.json(); }).catch(e => { cached.delete(url); throw e; })); return cached.get(url); }
  const stockCache = new Map(), stockQueue = [];
  let stockActive = 0;
  function stockTask(task) {
    return new Promise((resolve, reject) => { stockQueue.push({ task, resolve, reject }); pumpStock(); });
  }
  function pumpStock() {
    while (stockActive < 4 && stockQueue.length) {
      const item = stockQueue.shift(); stockActive++;
      Promise.resolve().then(item.task).then(item.resolve, item.reject).finally(() => { stockActive--; pumpStock(); });
    }
  }
  async function productStock(variant, sku, url) {
    const key = String(variant), existing = stockCache.get(key);
    if (existing && Date.now() - existing.time < 60000) return existing.promise;
    const promise = stockTask(async () => {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Product unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const metadata = page.querySelector('[data-shimano-product-stock]');
        if (metadata) {
          const data = JSON.parse(metadata.textContent);
          if (String(data.variant) !== key || norm(data.sku) !== norm(sku) || !data.price || !data.availability) throw new Error('Product changed');
          return data;
        }
        // Supports the previous Complete Cycles theme while previewing this release.
        const selected = page.querySelector('#VariantSelect option:checked') || page.querySelector('form.product-form [name="id"]');
        if (String(selected?.value) !== key || norm(page.querySelector('#ProductSku')?.textContent) !== norm(sku)) throw new Error('Product changed');
        const price = page.querySelector('#ProductPrice')?.textContent.trim();
        const availability = page.querySelector('#ProductStatus')?.textContent.trim();
        if (!price || !availability) throw new Error('Missing product details');
        return { variant: key, sku, price, availability };
      } finally { clearTimeout(timeout); }
    });
    stockCache.set(key, { time: Date.now(), promise });
    promise.catch(() => { if (stockCache.get(key)?.promise === promise) stockCache.delete(key); });
    return promise;
  }
  async function fillStock(node) {
    if (node.dataset.loading || !node.isConnected) return;
    node.dataset.loading = 'true';
    try {
      const stock = await productStock(node.dataset.variant, node.dataset.sku, node.dataset.url);
      node.replaceChildren(el('span', stock.price, 'sf-live-price'), el('span', stock.availability, 'sf-live-availability'));
    } catch (_) { node.textContent = 'Check product for price and availability'; }
  }
  const stockObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { stockObserver.unobserve(entry.target); fillStock(entry.target); }
  }, { rootMargin: '250px' }) : null;
  function observeStock(parent) {
    for (const node of parent.querySelectorAll('[data-shimano-live-stock]')) {
      if (stockObserver) stockObserver.observe(node); else fillStock(node);
    }
  }

  async function initSram(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const indexUrl = new URL(root.dataset.index, location.href), asset = name => new URL(name, indexUrl).href;
    const status = root.querySelector('.sf-status'), results = root.querySelector('.sf-results'), view = root.querySelector('.sf-view');
    const searchMode = root.dataset.mode === 'search';
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    const shopOrigin = root.dataset.shopOrigin || location.origin;
    const storeUrl = path => {
      const u = new URL(path, shopOrigin);
      if (preview) u.searchParams.set('preview_theme_id', preview);
      return u.origin === location.origin ? u.pathname + u.search : u.href;
    };
    let index, sequence = 0, currentDoc;
    function modelFor(query, document) {
      const q=norm(query);
      const component=Object.entries(index.components || {}).find(([sku,c])=>(norm(sku)===q || norm(c.source.upc)===q || norm(c.source.sku)===q) && c.models.some(m=>document.models.includes(m)));
      return component?.[1].models.find(m=>document.models.includes(m)) || query.trim().toUpperCase();
    }
    function say(message) { status.textContent = message; }
    function requestUrl(part, doc) {
      const u = new URL(root.dataset.search || '/search', shopOrigin);
      Object.entries({view:'sram-request',part:part.part,description:part.description,model:doc.title,
        models:doc.modelHeader,doc:doc.id,source:doc.source.file,page:part.page,callout:part.label,
        from:location.pathname + location.search}).forEach(([key,value]) => u.searchParams.set(key,String(value || '')));
      if (preview) u.searchParams.set('preview_theme_id',preview);
      return u.origin === location.origin ? u.pathname + u.search : u.href;
    }
    function purchase(parent, part, doc) {
      const matches = part.matches || [];
      if (!matches.length) {
        parent.append(link('REQUEST THIS PART ↗',requestUrl(part,doc),'sf-buy sf-request')); return;
      }
      for (const match of matches) {
        const url = storeUrl(`/variants/${match.variant}`), option = el('div',null,'sf-shop-option');
        const stock = el('div','Loading price and availability…','sf-live-stock');
        stock.dataset.shimanoLiveStock = '';stock.dataset.variant = match.variant;stock.dataset.sku = part.part;stock.dataset.url = url;stock.setAttribute('aria-live','polite');
        option.append(stock,link('VIEW PART ↗',url,'sf-buy'));
        parent.append(option);
      }
    }
    async function openDoc(id, model = '') {
      const seq = ++sequence;
      say('Loading original service pages…');
      try {
        const doc = await json(asset(id + '.json'));
        if (sequence !== seq) return;
        currentDoc = doc; view.replaceChildren();
        view.append(el('h3',doc.title),el('p',doc.modelHeader || 'Use the original table descriptions to select your variant.','sr-caption'));
        const source = el('p',`2026 ${doc.brand} catalogue · pages ${doc.pages.map(p=>p.page).join(', ')}. `,'sr-caption');
        source.append(link('Official service documents ↗',doc.source.sourceUrl,'',true));view.append(source);
        const controls=el('div',null,'sr-controls'),filterLabel=el('label','Filter parts in this diagram '),filter=el('input');filter.type='search';filter.placeholder='Size, travel, part number…';filterLabel.append(filter);controls.append(filterLabel);
        const modelKeys=Object.keys(doc.verifiedModelParts || {});
        let modelSelect;
        if (modelKeys.length) {
          const label=el('label','Model ');modelSelect=el('select');
          const all=el('option','All variants in the source table');all.value='';modelSelect.append(all);
          for(const key of modelKeys){const option=el('option',key+' — verified parts');option.value=key;modelSelect.append(option);}
          if(modelKeys.includes(model))modelSelect.value=model;
          label.append(modelSelect);controls.append(label);
        }
        view.append(controls,el('p','For shared diagrams, check each part’s full description and the notes on the original page for model, generation and size.','sr-caption'));
        const layout=el('div',null,'sr-layout'),pages=el('div',null,'sr-source-pages'),parts=el('div',null,'sr-parts');
        layout.append(pages,parts);view.append(layout);
        let selected=[];
        function shownRows(){
          const query=norm(filter.value),allowed=modelSelect?.value ? new Set(doc.verifiedModelParts[modelSelect.value]) : null;
          return doc.parts.filter(p=>(!allowed || allowed.has(p.part)) && (!query || norm(p.description+' '+p.part+' '+p.label).includes(query)));
        }
        function renderParts(){
          parts.replaceChildren();const visible=shownRows();
          for(const [i,p] of visible.entries()){
            const card=el('article',null,'sr-part');card.dataset.part=p.part;card.dataset.label=p.label;
            const label=el('small',`${p.label && p.label.toLowerCase()!=='ns' ? 'Callout '+p.label : 'Not shown / table part'} · source page ${p.page}`);
            card.append(label,el('h4',p.description || p.part),el('code',p.part));
            if(selected.includes(p.label))card.classList.add('sr-active');
            purchase(card,p,doc);parts.append(card);
          }
          if(!visible.length)parts.append(el('p','No parts match this filter.'));
          observeStock(parts);
        }
        function choose(labels, codes=[]){
          selected=labels;
          const applies=p=>codes.length ? codes.includes(p.part) : labels.includes(p.label);
          let visible=shownRows().filter(applies);
          if(!visible.length && filter.value){filter.value='';renderParts();visible=shownRows().filter(applies);}
          for(const card of parts.querySelectorAll('.sr-part'))card.classList.toggle('sr-active',codes.length ? codes.includes(card.dataset.part) : labels.includes(card.dataset.label));
          for(const button of pages.querySelectorAll('.sr-spot'))button.setAttribute('aria-pressed',String(button.dataset.labels.split('|').some(l=>labels.includes(l))));
          const active=parts.querySelector('.sr-active');
          if(active)active.scrollIntoView({behavior:'smooth',block:'nearest'});
          say(visible.length ? `${visible.length} source-listed part option${visible.length===1?'':'s'} for ${codes.length ? codes.join(', ') : 'callout '+labels.join(', ')}.` : `No part for this callout matches the selected model. Check the original source table.`);
        }
        for(const p of doc.pages){
          const figure=el('div',null,'sr-page'),img=el('img');img.src=asset(p.image);img.width=p.width;img.height=p.height;img.loading='lazy';img.alt=`Original ${doc.brand} catalogue page ${p.page}: ${doc.title}`;figure.append(img);
          for(const h of p.hotspots){
            const button=el('button',null,'sr-spot');button.type='button';button.dataset.labels=h.labels.join('|');button.setAttribute('aria-label','Show parts for '+(h.partCodes?.length ? h.partCodes.join(', ') : 'callout '+h.labels.join(', ')));button.setAttribute('aria-pressed','false');
            const [x,y,w,height]=h.box;Object.assign(button.style,{left:x+'%',top:y+'%',width:(w+.8)+'%',height:(height+.5)+'%'});
            button.addEventListener('click',()=>choose(h.labels,h.partCodes || []));figure.append(button);
          }
          pages.append(figure);
          const actions=el('p',null,'sr-page-actions');actions.append(el('span','Page '+p.page+' · '),link('Open original page at full size ↗',asset(p.image),'',true));pages.append(actions);
        }
        filter.addEventListener('input',renderParts);modelSelect?.addEventListener('change',()=>{selected=[];renderParts();});renderParts();
        say(`${doc.parts.length} source-listed part rows. Click an original callout to highlight its options.`);
        if(searchMode){const u=new URL(location.href);u.searchParams.set('doc',id);history.replaceState(null,'',u.pathname+u.search);}
      } catch (error) { if(sequence===seq)say('The service pages could not load. Please try again.'); }
    }
    function search(query, family = ''){
      const q=norm(query),words=String(query).trim().toUpperCase().split(/\s+/).filter(Boolean).map(norm);
      const docs=index.documents.filter(d=>(!family || d.family===family)).map(d=>{
        const main=norm(d.title+' '+d.models.join(' ')+' '+d.modelHeader),part=d.parts.some(p=>norm(p)===q);
        const component=Object.entries(index.components || {}).find(([sku,c])=>(norm(sku)===q || norm(c.source.upc)===q || norm(c.source.sku)===q) && c.models.some(m=>d.models.includes(m)));
        let score=0;
        if(!q)score=1;
        else if(d.models.some(m=>norm(m)===q))score=100 + 10 / Math.max(1,d.models.length);
        else if(part || component)score=90;
        else if(main.includes(q))score=80;
        else if(words.length && words.every(w=>main.includes(w)))score=60;
        else if(words.length && words.every(w=>norm(d.terms).includes(w)))score=20;
        return {d,score};
      }).filter(item=>item.score).sort((a,b)=>b.score-a.score || a.d.title.localeCompare(b.d.title));
      results.replaceChildren();view.replaceChildren();++sequence;
      let count=0;
      function more(){
        results.querySelector('.sr-more')?.remove();
        for(const {d} of docs.slice(count,count+40)){
          const detail=new URL(root.dataset.search || '/search',shopOrigin);
          detail.searchParams.set('view','sram-diagram');detail.searchParams.set('doc',d.id);
          if(query)detail.searchParams.set('q',query);
          const model=modelFor(query,d);if(d.models.includes(model))detail.searchParams.set('model',model);
          const row=el('div',null,'sr-result'),anchor=link(d.title+' ↗',storeUrl(detail.href),'sr-document-link');
          row.append(anchor,el('small',`${d.brand} · pages ${d.pages.join(', ')} · ${d.partCount} parts${d.modelHeader ? ' · '+d.modelHeader : ''}`));results.append(row);
        }
        count+=40;
        if(count<docs.length){const b=el('button','Show more diagrams','sr-more');b.type='button';b.addEventListener('click',more);results.append(b);}
      }
      more();say(`${docs.length} matching diagram / table group${docs.length===1?'':'s'}.`);
      if(!docs.length){const a=link('Ask us to find your part ↗',(root.dataset.search || '/search')+'?view=sram-request&model='+encodeURIComponent(query),'sf-buy');results.append(a);}
      if(q){const u=new URL(location.href);u.searchParams.set('q',query);u.searchParams.delete('doc');history.replaceState(null,'',u.pathname+u.search);}
      return docs;
    }
    try {
      index=await json(indexUrl.href);
      if(root.dataset.mode==='diagram'){
        root.hidden=false;
        const params=new URLSearchParams(location.search),doc=index.documents.find(d=>d.id===params.get('doc'));
        const back=new URL(root.dataset.search || '/search',shopOrigin);back.searchParams.set('view','exploded-diagrams');if(params.get('q'))back.searchParams.set('q',params.get('q'));
        results.append(link('← Back to exploded diagrams',storeUrl(back.href),'sr-back'));
        if(doc)await openDoc(doc.id,params.get('model') || modelFor(params.get('q') || '',doc));
        else say('This service diagram could not be found. Return to parts search to choose a diagram.');
      } else if(searchMode){
        const form=root.querySelector('form'),input=form.querySelector('input'),brand=form.querySelector('select');
        const params=new URLSearchParams(location.search),query=params.get('q') || '';input.value=query;
        form.addEventListener('submit',event=>{event.preventDefault();search(input.value,brand.value);});brand.addEventListener('change',()=>search(input.value,brand.value));
        const initial=search(query);
        const requested=params.get('doc'),requestedDocument=index.documents.find(d=>d.id===requested);
        if(requestedDocument)openDoc(requested,modelFor(query,requestedDocument));
      } else {
        const component=index.components?.[root.dataset.sku];
        // Only a labelled model ID can link a complete component to a source
        // document. Product titles and unlabelled description text cannot.
        const describedModels=[...(root.dataset.modelDescription || '').toUpperCase().matchAll(/\b(?:SRAM\s+)?MODEL\s+ID\s*:\s*([A-Z]{2}-[A-Z0-9]+(?:-[A-Z0-9]+)+)\b/g)].map(m=>m[1]);
        const models=[...new Set([...(component?.models || []),...describedModels])];
        const found=index.documents.filter(d=>d.parts.includes(root.dataset.sku) || models.some(m=>d.models.includes(m)));
        root.hidden=false;
        const select=document.getElementById('VariantSelect');select?.addEventListener('change',()=>{root.dataset.initialized='';root.dataset.sku=document.getElementById('ProductSku')?.textContent.trim() || '';root.hidden=true;results.replaceChildren();view.replaceChildren();initSram(root);},{once:true});
        if(!found.length){say('No exact source model or service-part match is recorded for this product. Browse service diagrams above using the manufacturer model ID.');return;}
        say(models.length ? 'Service diagrams matched to the manufacturer model ID recorded for this product.' : 'This service part appears in these original source tables.');
        for(const d of found){const row=el('div',null,'sr-result'),button=el('button',d.title);button.type='button';button.addEventListener('click',()=>openDoc(d.id,models.find(m=>d.models.includes(m)) || ''));row.append(button);results.append(row);}
        // Show the primary diagram and service parts on the product page itself.
        const primary=found.find(d=>d.models.length===1 && models.includes(d.models[0])) || found[0];
        await openDoc(primary.id,models.find(m=>primary.models.includes(m)) || '');
      }
    } catch(error){root.hidden=false;say('The parts finder could not load. Please try again or browse service diagrams above.');}
  }
  document.querySelectorAll('[data-sram-family]').forEach(initSram);
})();
